import {
    ColDef,
    ColGroupDef,
    ICellRendererParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { t } from 'i18next';
import {
    JOB_LABEL,
    JOB_NUMBER,
    AGENT_SOFTWARE_ID,
    MANTY_STATUS,
    ORG_CHART_HR_SOFTWARE_ID,
    RECURRENCE,
    ORG_CHART_HR_NAME,
} from 'routes/HumanResourcesModule/business/definitions';
import {
    agentCellRendererSelectorGetter,
    getGroupedAgentColumn,
} from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/agentColumnHelpers';
import { TEXT_FILTER } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/defaultColDefs';
import { getGroupedOrgChartHrColumn } from 'routes/HumanResourcesModule/components/HrDataGrid/helpers/orgChartHrColumnHelpers';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import {
    createLoadedSimulatedColumn,
    createRealizedColumn,
    getAmountColumns,
    isRowGrouped,
    isRowPinned,
    isFooterRow,
} from 'routes/HumanResourcesModule/Simulation/helpers/gridHelpers';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';

import { SemiTreatmentHypothesis } from 'routes/HumanResourcesModule/Simulation/helpers/hooks/semiTreatment';
import { PAY_COMPONENT } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/helpers/constants';
import { PayComponent } from 'routes/HumanResourcesModule/PayState/helpers/constants';
import {
    AgentPayComponentHypothesis,
    SimulationAgentPayComponentData,
} from '../components/PayComponent/helpers/types';
import { AgentDepartureHypothesis } from '../components/Departure/helpers/types';
import {
    AGENT_DEPARTURE_HYPOTHESIS,
    AGENT_DEPARTURE_HYPOTHESIS_TYPE,
} from '../components/Departure/helpers/constants';
import { AgentHypothesesCellRenderValue, SimulationAgentData } from './types';
import { NEW_ARRIVAL } from './constants';
import { GradeAdvancementHypothesis } from '../components/GradeAdvancement/helpers/types';
import { UNMAPPED } from 'business/constants';
import { AgentDataGridContext } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/types';
import { getMantyStatusColumn } from 'routes/HumanResourcesModule/PayState/helpers/colDefinitions';

const isArrivalHypothesisLine = (data: SimulationAgentData) =>
    data[AGENT_SOFTWARE_ID]?.includes(NEW_ARRIVAL);

/*
 * Find an hypothesis on an existing agent.
 * Existing agent are agent originally present in the simulated data, hypothesis are linked
 * to their manty_status, software_id, job_number
 */
const findExistingAgentDepartureHypothesis = <
    T extends AgentDepartureHypothesis,
>(
    data: SimulationAgentData,
    hypothesesData: T[],
): T | undefined =>
    hypothesesData.find(
        (hypothesis) =>
            data[AGENT_SOFTWARE_ID] === hypothesis[AGENT_SOFTWARE_ID] &&
            (hypothesis[MANTY_STATUS] === data[MANTY_STATUS] ||
                hypothesis[MANTY_STATUS] === undefined) &&
            // A null job number means that the hypotheses holds for the agent
            // across all of their job assignments.
            (hypothesis[JOB_NUMBER] === data[JOB_NUMBER] ||
                hypothesis[JOB_NUMBER] === null),
    );

/*
 * Find a grade advancement hypothesis on an existing agent.
 */
const findExistingGradeAdvancementHypothesis = <
    T extends GradeAdvancementHypothesis,
>(
    data: SimulationAgentData,
    hypothesesData: T[],
    recurrence: RecurrenceEnum,
): T | undefined =>
    hypothesesData.find(
        (hypothesis) =>
            data[AGENT_SOFTWARE_ID] === hypothesis[AGENT_SOFTWARE_ID] &&
            data[ORG_CHART_HR_SOFTWARE_ID] ===
                hypothesis[ORG_CHART_HR_SOFTWARE_ID] &&
            recurrence === hypothesis[RECURRENCE] &&
            (hypothesis[MANTY_STATUS] === data[MANTY_STATUS] ||
                hypothesis[MANTY_STATUS] === undefined) &&
            // A null job number means that the hypotheses holds for the agent
            // across all of their job assignments.
            (hypothesis[JOB_NUMBER] === data[JOB_NUMBER] ||
                hypothesis[JOB_NUMBER] === null),
    );
const isHypIncludedInOrgChartHrs = <T extends AgentPayComponentHypothesis>(
    data: SimulationAgentData | SimulationAgentPayComponentData,
    hypothesis: T,
) =>
    data[ORG_CHART_HR_SOFTWARE_ID] === hypothesis[ORG_CHART_HR_SOFTWARE_ID] ||
    (data[ORG_CHART_HR_SOFTWARE_ID] === 'unmapped' &&
        data[ORG_CHART_HR_NAME].includes(
            hypothesis[ORG_CHART_HR_SOFTWARE_ID].slice(-5),
        ));
export const findExistingAgentPayComponentHypothesis = <
    T extends AgentPayComponentHypothesis,
>(
    data: SimulationAgentData | SimulationAgentPayComponentData,
    hypothesesData: T[],
    recurrence: RecurrenceEnum,
    payComponent: string,
): T | undefined => {
    const hypothesis = hypothesesData.find(
        (hyp) =>
            data[AGENT_SOFTWARE_ID] === hyp[AGENT_SOFTWARE_ID] &&
            isHypIncludedInOrgChartHrs(data, hyp) &&
            (hyp[MANTY_STATUS] === data[MANTY_STATUS] ||
                hyp[MANTY_STATUS] === undefined) &&
            // A null job number means that the hypotheses holds for the agent
            // across all of their job assignments.
            (hyp[JOB_NUMBER] === data[JOB_NUMBER] ||
                hyp[JOB_NUMBER] === null) &&
            recurrence === hyp[RECURRENCE] &&
            payComponent === hyp[PAY_COMPONENT],
    );
    return hypothesis;
};

const agentHypothesisValueGetter =
    (recurrence: RecurrenceEnum) =>
    (
        params: ValueGetterParams<SimulationAgentData | SimulationAgentData[]>,
    ): AgentHypothesesCellRenderValue => {
        const {
            agentArrivalHypothesesData,
            agentDepartureHypothesesData,
            agentPayComponentHypothesesData,
            gradeAdvancementHypothesesData,
        } = params.context as AgentDataGridContext;
        if (
            !params.data ||
            !agentArrivalHypothesesData ||
            !agentDepartureHypothesesData ||
            !agentPayComponentHypothesesData ||
            !gradeAdvancementHypothesesData
        )
            return undefined;
        // If data is a type of an array, it means the row is grouped and we do not have any value then
        if (Array.isArray(params.data) || isRowPinned(params)) return undefined;

        const { data } = params;

        return {
            agentArrivalHypothesis: isArrivalHypothesisLine(params.data)
                ? agentArrivalHypothesesData.find(
                      (arrivalHypothesis) =>
                          (data[ORG_CHART_HR_SOFTWARE_ID] === UNMAPPED &&
                              data[ORG_CHART_HR_NAME].includes(
                                  arrivalHypothesis[ORG_CHART_HR_SOFTWARE_ID],
                              )) ||
                          data[AGENT_SOFTWARE_ID] ===
                              arrivalHypothesis[AGENT_SOFTWARE_ID],
                  )
                : undefined,
            agentDepartureHypothesis: findExistingAgentDepartureHypothesis(
                data,
                agentDepartureHypothesesData,
            ),
            ifsePayComponentHypotheses: findExistingAgentPayComponentHypothesis(
                data,
                agentPayComponentHypothesesData,
                recurrence,
                PayComponent.IFSE_AND_MONTHLY_PRIMES,
            ),
            ponctualPrimesPayComponentHypotheses:
                findExistingAgentPayComponentHypothesis(
                    data,
                    agentPayComponentHypothesesData,
                    recurrence,
                    PayComponent.PONCTUAL_PRIMES,
                ),
            gradeAdvancementHypothesis: findExistingGradeAdvancementHypothesis(
                data,
                gradeAdvancementHypothesesData,
                recurrence,
            ),
        };
    };

const agentHypothesisFilterValueGetter =
    (recurrence: RecurrenceEnum) =>
    (params: ValueGetterParams<SimulationAgentData>) => {
        // Use the value getter logic to find the relevant hypothesis
        const hypothesisValueGetter = agentHypothesisValueGetter(recurrence);

        // Get the hypothesis value using the existing logic
        const hypothesisValue = hypothesisValueGetter(params);

        // Map the value to the appropriate translation key
        if (hypothesisValue) {
            const translations = [];

            if (hypothesisValue.agentDepartureHypothesis) {
                translations.push(
                    t(
                        `humanResources.simulation.agentDepartureHypothesisType.${hypothesisValue.agentDepartureHypothesis[AGENT_DEPARTURE_HYPOTHESIS_TYPE]}`,
                    ),
                );
            }

            if (hypothesisValue.ponctualPrimesPayComponentHypotheses) {
                translations.push(
                    t(
                        `humanResources.simulation.agentPayComponentHypothesisType.simple_prime`,
                    ),
                );
            }
            if (hypothesisValue.ifsePayComponentHypotheses) {
                translations.push(PayComponent.IFSE_AND_MONTHLY_PRIMES);
            }

            if (hypothesisValue.agentArrivalHypothesis) {
                translations.push(
                    t(
                        `humanResources.simulation.agentArrivalHypothesisType.simple_arrival`,
                    ),
                );
            }
            if (hypothesisValue.gradeAdvancementHypothesis) {
                translations.push(
                    t(
                        `humanResources.simulation.agentHypothesis.gradeHypotheses`,
                    ),
                );
            }

            return translations.length > 0
                ? translations.join(', ')
                : undefined;
        }

        return undefined;
    };

const agentHypothesisCellRendererSelector = (params: ICellRendererParams) => {
    if (isFooterRow(params)) return { component: undefined };
    if (isRowGrouped(params)) return { component: undefined };
    if (isRowPinned(params)) return { component: undefined };
    if (params.data && isArrivalHypothesisLine(params.data)) {
        return { component: 'updateAgentArrivalHypothesisCellRenderer' };
    }
    return { component: 'agentHypothesisCellRenderer' };
};

export const getColDefs = ({
    recurrence,
    hrSimulation,
    semiTreatmentHypothesesData,
}: {
    recurrence: RecurrenceEnum;
    hrSimulation: HrSimulation;
    semiTreatmentHypothesesData: SemiTreatmentHypothesis[];
}): (ColDef | ColGroupDef)[] => {
    const betweenCurrentAndSimulatedDateColumnGetters = [
        createRealizedColumn,
        createLoadedSimulatedColumn,
    ];
    const pastDateColumnGetters = [createRealizedColumn];
    const simulatedDateColumnGetters = [createLoadedSimulatedColumn];
    const columns: (ColDef | ColGroupDef)[] = [
        getGroupedOrgChartHrColumn(),
        {
            ...getGroupedAgentColumn(
                false,
                agentCellRendererSelectorGetter(
                    semiTreatmentHypothesesData,
                    true,
                    hrSimulation.recurrentInitialisationStartDate,
                ),
            ),
            width: 110,
            pinned: 'left',
        },
        getMantyStatusColumn(),

        ...[JOB_LABEL, JOB_NUMBER].map(
            (field): ColDef => ({
                ...TEXT_FILTER,
                headerName: t(`humanResources.simulation.${field}`),
                field,
                enableRowGroup: true,
                hide: recurrence === RecurrenceEnum.NON_RECURRENT,
            }),
        ),
        {
            ...TEXT_FILTER,
            headerName: t('humanResources.simulation.hypothesis'),
            field: AGENT_DEPARTURE_HYPOTHESIS,
            valueGetter: agentHypothesisValueGetter(recurrence),
            cellRendererSelector: agentHypothesisCellRendererSelector,
            filterValueGetter: agentHypothesisFilterValueGetter(recurrence),
            cellRendererParams: {
                hrSimulation,
                recurrence,
            },
            width: 210,
            pinned: 'left',
        },
        ...getAmountColumns({
            simulationStart: hrSimulation.startDate,
            simulationEnd: hrSimulation.endDate,
            betweenCurrentAndSimulatedDateColumnGetters,
            pastDateColumnGetters,
            simulatedDateColumnGetters,
        }),
    ];
    return columns;
};
